import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaLock, FaShieldAlt, FaSignInAlt, FaUserPlus } from "react-icons/fa";
import LoginPopup from "../LogIn/LoginPopup";
import { supabase } from "../../supabaseClient";
import "../../styles/ProtectedFormGate.css";

const getFallbackProfile = (currentUser) => {
  if (!currentUser) return null;

  const metadata = currentUser.user_metadata || {};

  return {
    full_name: metadata.full_name || metadata.name || "",
    phone_number: metadata.phone || "",
    email: currentUser.email || "",
  };
};

function ProtectedFormGate({
  children,
  title = "Sign in to continue",
  description = "Create an account or log in to submit this inquiry.",
  contextLabel = "Account required",
  formRef,
  onAuthenticated,
}) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [initialSignup, setInitialSignup] = useState(false);
  const pendingSubmit = useRef(false);

  const hydrateProfile = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      return;
    }

    const fallbackProfile = getFallbackProfile(currentUser);
    setProfile(fallbackProfile);

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, phone_number, email")
        .eq("id", currentUser.id)
        .maybeSingle();

      if (error) throw error;

      setProfile({
        ...fallbackProfile,
        ...(data || {}),
        email: data?.email || fallbackProfile.email,
      });
    } catch (error) {
      setProfile(fallbackProfile);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeGate = async () => {
      setLoading(true);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        const currentUser = session?.user ?? null;
        setUser(currentUser);
        setLoading(false);

        if (currentUser) hydrateProfile(currentUser);
      } catch (error) {
        if (mounted) setLoading(false);
      }
    };

    initializeGate();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        setProfile(getFallbackProfile(currentUser));
        setTimeout(() => {
          if (mounted) hydrateProfile(currentUser);
        }, 0);
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateProfile]);

  useEffect(() => {
    if (user && onAuthenticated) {
      onAuthenticated({ user, profile: profile || getFallbackProfile(user) });
    }
  }, [onAuthenticated, profile, user]);

  useEffect(() => {
    if (!user || !pendingSubmit.current || !formRef?.current) return;

    pendingSubmit.current = false;
    setShowLogin(false);

    setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 0);
  }, [formRef, user]);

  const openLogin = (signupMode) => {
    setInitialSignup(signupMode);
    setShowLogin(true);
  };

  const guardSubmit = (event, submitHandler) => {
    if (loading) {
      event.preventDefault();
      return;
    }

    if (user) {
      submitHandler(event);
      return;
    }

    event.preventDefault();
    pendingSubmit.current = true;
    openLogin(false);
  };

  const gateContent =
    typeof children === "function"
      ? children({
          guardSubmit,
          isAuthenticated: Boolean(user),
          loading,
          openLogin,
          profile: profile || getFallbackProfile(user),
          user,
        })
      : children;

  if (loading) {
    return (
      <>
        <div className="protected-submit-note">
          <div className="protected-note-icon">
            <div className="protected-gate-loader" />
          </div>
          <div>
            <strong>Checking account access...</strong>
            <span>Your form will be ready in a moment.</span>
          </div>
        </div>
        {gateContent}
      </>
    );
  }

  if (user) {
    return gateContent;
  }

  return (
    <>
      <div className="protected-submit-note">
        <div className="protected-note-icon">
          <FaLock />
        </div>
        <div>
          <span className="protected-note-kicker">
            <FaShieldAlt />
            {contextLabel}
          </span>
          <strong>{title}</strong>
          <span>{description}</span>
        </div>
        <div className="protected-note-actions">
          <button type="button" className="protected-note-primary" onClick={() => openLogin(false)}>
            <FaSignInAlt />
            Login
          </button>
          <button type="button" className="protected-note-secondary" onClick={() => openLogin(true)}>
            <FaUserPlus />
            Create Account
          </button>
        </div>
      </div>

      {gateContent}

      {showLogin && (
        <LoginPopup
          initialSignup={initialSignup}
          onClose={() => {
            setShowLogin(false);
            setInitialSignup(false);
          }}
        />
      )}
    </>
  );
}

export default ProtectedFormGate;
